import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Types
interface User {
  id: string;
  email: string;
  name: string | null;
}

export interface FormAnswer {
  id: string;
  userId: string;
  answers: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  formData: FormAnswer | null;
  formAnswerId: string;
  isSubmitting: boolean;
  isLoading: boolean;
  error: Error | null;
  isQuestionnaireSubmitted: boolean;
  questionnaireSubmission: 'none' | 'complete' | 'skipped';
}

// Base state types
interface UIState {
  isSidebarOpen: boolean;
  theme: 'light' | 'dark';
  language: string;
}

interface AuthState {
  user: User | null;
  status: 'idle' | 'loading' | 'authenticated' | 'error';
  error: Error | null;
}

interface AppState {
  ui: UIState;
  auth: AuthState;
  form: FormState;
}

// Create store
export const useStore = create<AppState>()(
  persist(
    (set) => ({
      ui: {
        isSidebarOpen: true,
        theme: 'light',
        language: 'en',
      },
      auth: {
        user: null,
        status: 'idle',
        error: null,
      },
      form: {
        formData: null,
        formAnswerId: '',
        isSubmitting: false,
        isLoading: false,
        error: null,
        isQuestionnaireSubmitted: false,
        questionnaireSubmission: 'none',
      },
    }),
    {
      name: 'app-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// UI actions
export const uiActions = {
  toggleSidebar: () =>
    useStore.setState((state) => ({
      ui: { ...state.ui, isSidebarOpen: !state.ui.isSidebarOpen },
    })),
  setTheme: (theme: 'light' | 'dark') =>
    useStore.setState((state) => ({
      ui: { ...state.ui, theme },
    })),
  setLanguage: (language: string) =>
    useStore.setState((state) => ({
      ui: { ...state.ui, language },
    })),
};

// Auth actions
export const authActions = {
  setUser: (user: User | null) =>
    useStore.setState((state) => ({
      auth: { ...state.auth, user, status: user ? 'authenticated' : 'idle' },
    })),
  setError: (error: Error | null) =>
    useStore.setState((state) => ({
      auth: { ...state.auth, error, status: error ? 'error' : 'idle' },
    })),
  setStatus: (status: AuthState['status']) =>
    useStore.setState((state) => ({
      auth: { ...state.auth, status },
    })),
  logout: () =>
    useStore.setState((state) => ({
      auth: { user: null, status: 'idle', error: null },
    })),
};

// Form actions
export const formActions = {
  setFormData: (formData: FormAnswer | null) =>
    useStore.setState((state) => ({
      form: { ...state.form, formData },
    })),
  setFormAnswerId: (formAnswerId: string) =>
    useStore.setState((state) => ({
      form: { ...state.form, formAnswerId },
    })),
  setIsSubmitting: (isSubmitting: boolean) =>
    useStore.setState((state) => ({
      form: { ...state.form, isSubmitting },
    })),
  setIsLoading: (isLoading: boolean) =>
    useStore.setState((state) => ({
      form: { ...state.form, isLoading },
    })),
  setError: (error: Error | null) =>
    useStore.setState((state) => ({
      form: { ...state.form, error },
    })),
  setQuestionnaireSubmitted: (isSubmitted: boolean) =>
    useStore.setState((state) => ({
      form: { ...state.form, isQuestionnaireSubmitted: isSubmitted },
    })),
  setQuestionnaireSubmission: (submission: 'none' | 'complete' | 'skipped') =>
    useStore.setState((state) => ({
      form: { ...state.form, questionnaireSubmission: submission },
    })),
  resetForm: () =>
    useStore.setState((state) => ({
      form: {
        formData: null,
        formAnswerId: '',
        isSubmitting: false,
        isLoading: false,
        error: null,
        isQuestionnaireSubmitted: false,
        questionnaireSubmission: 'none',
      },
    })),
};

// Selectors
export const selectUser = (state: AppState) => state.auth.user;
export const selectAuthStatus = (state: AppState) => state.auth.status;
export const selectAuthError = (state: AppState) => state.auth.error;
export const selectTheme = (state: AppState) => state.ui.theme;
export const selectLanguage = (state: AppState) => state.ui.language;
export const selectIsSidebarOpen = (state: AppState) => state.ui.isSidebarOpen;

// Form selectors
export const selectFormData = (state: AppState) => state.form.formData;
export const selectFormAnswerId = (state: AppState) => state.form.formAnswerId;
export const selectIsFormSubmitting = (state: AppState) =>
  state.form.isSubmitting;
export const selectIsFormLoading = (state: AppState) => state.form.isLoading;
export const selectFormError = (state: AppState) => state.form.error;
export const selectIsQuestionnaireSubmitted = (state: AppState) =>
  state.form.isQuestionnaireSubmitted;
export const selectQuestionnaireSubmission = (state: AppState) =>
  state.form.questionnaireSubmission;

// Export all stores
export * from './form.store';
export * from './advice.store';
export * from './user.store';
export * from './auth.store';
export * from './storage.store';
export * from './payments.store';
export * from './admin.store';
export * from './metrics.store';
